import { action, observable, runInAction, makeObservable } from "mobx";
import { randmarApiWithTags } from "randmar-api-client";
import { store } from "../../new-store/store";
import { Opportunity } from "./Opportunity";

class OpportunityStore {
  opportunities: Opportunity[] = [];
  opportunity?: Opportunity = undefined;

  constructor() {
    makeObservable(this, {
      opportunities: observable,
      opportunity: observable,
      getOpportunities: action.bound,
      getOpportunity: action.bound,
      addOpportunity: action.bound,
      renewOpportunity: action.bound,
      editOpportunity: action.bound,
      deleteOpportunity: action.bound,
      addResellerToOpportunity: action.bound,
      removeResellerFromOpportunity: action.bound,
      addProductToOpportunity: action.bound,
      deleteProductFromOpportunity: action.bound
    });
  }

  async getOpportunities(applicationId: string) {
    try {
      const response = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdOpportunities.initiate({
          routeManufacturerId: applicationId,
        })
      ).unwrap();

      const list = Array.isArray(response) ? response : [];
      const converted = list.map(Opportunity.convertFrom);

      runInAction(() => {
        this.opportunities = converted;
      });
    } catch (e) {
      console.error(e);
    }
  }

  async getOpportunity(applicationId: string, bidNumber: string, withSpecification: boolean) {
    try {
      const opportunity = await store.dispatch(
        randmarApiWithTags.endpoints.getV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumber.initiate({
          routeManufacturerId: applicationId,
          bidNumber,
          withSpecification,
        })
      ).unwrap();
      const converted = Opportunity.convertFrom(opportunity);

      runInAction(() => {
        this.opportunity = converted;
      });
    } catch (e) {
      console.error(e);
    }
  }

  async addOpportunity(
    applicationId: string,
    bidNumber: string,
    bidTitle: string,
    description: string,
    defaultResellers: string,
    startDate: string,
    endDate: string,
    quoteOnly: boolean
  ): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.putV4ManufacturerByRouteManufacturerIdOpportunity.initiate({
          routeManufacturerId: applicationId,
          createOpportunityInput: {
            BidNumber: bidNumber,
            BidTitle: bidTitle,
            Description: description,
            DefaultResellers: defaultResellers,
            StartDate: startDate,
            EndDate: endDate,
            QuoteOnly: quoteOnly,
          },
        })
      ).unwrap();
      return true;
    } catch {
      return false;
    }
  }

  async renewOpportunity(
    applicationId: string,
    bidNumber: string,
    newBidNumber: string,
    newBidTitle: string,
    newStartDate: string,
    newEndDate: string,
    quoteOnly: boolean
  ): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.putV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumber.initiate({
          routeManufacturerId: applicationId,
          bidNumber,
          renewOpportunityInput: {
            BidNumber: newBidNumber,
            BidTitle: newBidTitle,
            StartDate: newStartDate,
            EndDate: newEndDate,
            QuoteOnly: quoteOnly,
          },
        })
      ).unwrap();
      return true;
    } catch {
      return false;
    }
  }

  async editOpportunity(
    applicationId: string,
    bidNumber: string,
    fieldId: string,
    newValue: string
  ): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberFieldName.initiate({
          routeManufacturerId: applicationId,
          bidNumber,
          fieldName: fieldId,
          newValue,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async deleteOpportunity(applicationId: string, bidNumber: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.deleteV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumber.initiate({
          routeManufacturerId: applicationId,
          bidNumber,
        })
      ).unwrap();
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async addResellerToOpportunity(applicationId: string, bidNumber: string, resellerId: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.putV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberResellerResellerId.initiate({
          routeManufacturerId: applicationId,
          bidNumber,
          resellerId,
        })
      ).unwrap();
      return true;
    } catch {
      return false;
    }
  }

  async removeResellerFromOpportunity(applicationId: string, bidNumber: string, resellerId: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.deleteV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberResellerResellerId.initiate({
          routeManufacturerId: applicationId,
          bidNumber,
          resellerId,
        })
      ).unwrap();
      return true;
    } catch {
      return false;
    }
  }

  async addProductToOpportunity(applicationId: string, bidNumber: string, randmarSKU: string, bidPrice: number, bidPriceType: string): Promise<boolean> {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.postV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberPartNumberRandmarSku.initiate({
          routeManufacturerId: applicationId,
          bidNumber,
          randmarSku: randmarSKU,
          bidPrice,
          bidPriceType,
        })
      ).unwrap();
      return true;
    } catch {
      return false;
    }
  }

  async deleteProductFromOpportunity(applicationId: string, bidNumber: string, randmarSKU: string) {
    try {
      await store.dispatch(
        randmarApiWithTags.endpoints.deleteV4ManufacturerByRouteManufacturerIdOpportunityAndBidNumberPartNumberRandmarSku.initiate({
          routeManufacturerId: applicationId,
          bidNumber,
          randmarSku: randmarSKU,
        })
      ).unwrap();
      return true;
    } catch {
      return false;
    }
  }
}

export default OpportunityStore;
